import { useEffect, useMemo, useState } from 'react';
import { useCurrency } from '../context/CurrencyContext';
import { buildDisplayRateTable } from '../utils/displayRates';
import { chartRates, PerUsdOn } from '../utils/displaySeries';
import { warmRatesForDates } from '../utils/fxRates';

/** UTC ISO date for today, matching how chart series date their points. */
export function todayUtc(): string {
  return new Date().toISOString().slice(0, 10);
}

/**
 * Per-date USD → display-currency rates for a chart's dates (see `chartRates`).
 *
 * The bundled RBA table answers most dates synchronously, but it ends at its last
 * build, and the ECB cache that covers the gap starts empty. So the dates on
 * screen are warmed first, and the table is rebuilt once they land: a table
 * remembers a miss as today's spot, so the old one would never see them.
 *
 * The returned object changes identity only when the currency, the spot rate, or
 * the warmed data changes, so it is safe to depend on in a chart's effects.
 *
 * The table reads strictly at the RBA bundle's end (dates after it come from
 * ECB, never the bundle's stale last rate) and carries the previous rate forward
 * for a date nothing covers. Either, left as it once was, put a step in the line
 * at one arbitrary day boundary.
 */
export function useDatedDisplayRates(dates: readonly string[]): PerUsdOn {
  const { currency, rate } = useCurrency();
  const code = currency.code;

  const [fxVersion, setFxVersion] = useState(0);
  const datesKey = dates.join(',');
  useEffect(() => {
    if (code === 'USD' || !datesKey) return;
    const controller = new AbortController();
    warmRatesForDates(datesKey.split(','), { signal: controller.signal, currency: code })
      // Rebuild on every COMPLETED warm, not only when this run fetched something:
      // a run aborted part-way (the chart's dates changed mid-warm) leaves dates
      // cached that the next run then counts as "already there", and gating on
      // `fetched > 0` meant the table that remembered them as misses was kept.
      .then(() => {
        if (!controller.signal.aborted) setFxVersion((v) => v + 1);
      })
      .catch(() => {
        /* the affected days carry the previous rate forward; nothing to surface */
      });
    return () => controller.abort();
  }, [datesKey, code]);

  return useMemo(
    () => chartRates(buildDisplayRateTable(code, rate), rate, todayUtc()),
    // fxVersion: rebuild once warmed rates arrive (see above).
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [code, rate, fxVersion]
  );
}
